import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Fab, Tooltip } from "@mui/material";
import Comment from '@mui/icons-material/Comment';

import './Chat.css';

import { Campaign, Conversation, ConvoSeenTimes, Receivable, useAdminReceivableAlerts, useCampaignsReceivableAlerts, useUserReceivableAlerts } from '@dndModels';

import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDAPI from '@utilities/smart_api';
import FlexBox from '@components/layout/FlexBox';
import ChatBox from './ChatBox';
import ToastBox from '@components/display/ToastBox';
import InfoBox from '@components/input/InfoBox';

interface Props {
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ChatsButton({ 
}: Props) {
  const [received_ids, setReceivedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [processing, setProcessing] = useState(false);
  const [ping_time, setPingTime] = useState(0);
  const [deleting_convo, setDeletingConvo] = useState<Conversation | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const [toast_msg, setToastMsg] = useState("");

  // Refs so async subscription callbacks always read the CURRENT state,
  // rather than whatever was captured in the closure when connect() fired.
  const conversationsRef = useRef<Conversation[]>([]);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const receivedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    receivedIdsRef.current = received_ids;
  }, [received_ids]);

  const ping = () => {
    setPingTime(common_util.get_time_number());
  }

  const onUserReceivable = useCallback(async (receivable: Receivable) => {
    if (receivable._id && !receivedIdsRef.current.includes(receivable._id)) {
      setReceivedIds(prev => [...prev, receivable._id]);
      const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
      if (processed_group.length === 1) {
        const processed = processed_group[0];
        if (processed.origin_id !== api.temp_id) {
          // See if there's already a convo for it.
          let convo = conversationsRef.current.find(o => processed.conversation_id === o._id);
          if (convo) {
            if (processed.temp) {
              // It's probably deleting the conversation.
              if (processed.message === "Conversation Deleted") {
                // We don't actually delete the convo. We just delete the messages in it and mark it hidden.
                convo.messages = [];
                convo.hidden = true;
                setPingTime(common_util.get_time_number());
                setToastTime(common_util.get_time_number());
                setToastMsg(`Conversation with ${convo.name} has been deleted.`);
              }
            } else {
              convo.hidden = false;
              convo.messages.push(processed);
              if (convo.open) {
                if (api.user) {
                  api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
                  await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
                }
              } else {
                convo.flashing = true;
                setToastTime(common_util.get_time_number());
                setToastMsg(`${convo.name} says: ${processed.message}`);
              }
            }
          } else if (!processed.temp) { // Ignore it if it's temp. We already don't have it in memory, so we don't need it right now.
            // We don't already have the conversation, so it's a new one. 
            // Retrieve it.
            const newConvo = await api.getFullObject<Conversation>("conversation", processed.conversation_id);
            const user = api.my_users.find(o => o._id === processed.from_id);
            if (user && newConvo) {
              newConvo.name = user.name;
              newConvo.flashing = true;
              await api.loadConversation(newConvo);
              setConversations(prev => [...prev, newConvo]);
              setToastTime(common_util.get_time_number());
              setToastMsg(`${newConvo.name} says: ${processed.message}`);
            }
          }
          setReloading(true);
          ping();
        }
      }
    }
  }, []);

  const onAdminReceivable = useCallback(async (receivable: Receivable) => {
    console.log(receivable);
    if (receivable._id && !receivedIdsRef.current.includes(receivable._id)) {
      setReceivedIds(prev => [...prev, receivable._id]);
      const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
      if (processed_group.length === 1) {
        const processed = processed_group[0];
        if (processed.origin_id !== api.temp_id) {
          // See if there's already a convo for it.
          let convo = conversationsRef.current.find(o => processed.conversation_id === o._id);
          console.log(conversationsRef.current);
          console.log(convo);
          if (convo) {
            if (processed.temp) {
              // It's probably deleting the conversation.
              if (processed.message === "Conversation Deleted") {
                // We don't actually delete the convo. We just delete the messages in it and mark it hidden.
                convo.messages = [];
                convo.hidden = true;
                setPingTime(common_util.get_time_number());
                setToastTime(common_util.get_time_number());
                setToastMsg(`Conversation with ${convo.name} has been deleted.`);
              }
            } else {
              convo.hidden = false;
              convo.messages.push(processed);
              if (convo.open) {
                if (api.user) {
                  api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
                  await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
                }
              } else {
                convo.flashing = true;
                setToastTime(common_util.get_time_number());
                setToastMsg(`${convo.name} says: ${processed.message}`);
              }
            }
          } else if (!processed.temp) { // Ignore it if it's temp. We already don't have it in memory, so we don't need it right now.
            // We don't already have the conversation, so it's a new one. 
            // Retrieve it.
            const newConvo = await api.getFullObject<Conversation>("conversation", processed.conversation_id);
            const user = api.my_users.find(o => o._id === processed.from_id);
            if (user && newConvo) {
              newConvo.name = user.name;
              newConvo.flashing = true;
              await api.loadConversation(newConvo);
              setConversations(prev => [...prev, newConvo]);
              setToastTime(common_util.get_time_number());
              setToastMsg(`${newConvo.name} says: ${processed.message}`);
            }
          }
          setReloading(true);
          ping();
        }
      }
    }
  }, []);

  const onCampaignReceivable = useCallback(async (campaign_id: string, receivable: Receivable) => {
    if (receivable._id && !receivedIdsRef.current.includes(receivable._id)) {
      setReceivedIds(prev => [...prev, receivable._id]);
      const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
      if (processed_group.length === 1) {
        const processed = processed_group[0];
        if (processed.origin_id !== api.temp_id) {
          if (processed.type === "Chat") {
            const from = api.my_users.find(o => o._id === processed.from_id);
            if (from) {
              // See if there's already a convo for it.
              let convo = conversationsRef.current.find(o => processed.conversation_id === o._id);
              if (convo) {
                if (processed.temp) {
                  // It's probably deleting the conversation.
                  if (processed.message === "Conversation Deleted") {
                    // We don't actually delete the convo. We just delete the messages in it and mark it hidden.
                    convo.messages = [];
                    convo.hidden = true;
                    setPingTime(common_util.get_time_number());
                    setToastTime(common_util.get_time_number());
                    setToastMsg(`Conversation with ${convo.name} has been deleted.`);
                  }
                } else {
                  convo.hidden = false;
                  convo.messages.push(processed);
                  if (convo.open) {
                    if (api.user) {
                      api.user.convo_seen_times.setSeenTime(convo._id, common_util.get_time_number());
                      await api.upsertObject("convo_seen_times", api.user.convo_seen_times);
                    }
                  } else {
                    convo.flashing = true;
                    setToastTime(common_util.get_time_number());
                    setToastMsg(`In ${convo.name} Campaign ${from.name} says: ${processed.message}`);
                  }
                }
              } else if (!processed.temp) { // Ignore it if it's temp. We already don't have it in memory, so we don't need it right now.
                // We don't already have the conversation, so it's a new one. 
                // Retrieve it.
                const newConvo = await api.getFullObject<Conversation>("conversation", processed.conversation_id);
                const campaign = api.my_campaigns.find(o => o._id === processed.campaign_id);
                if (campaign && newConvo) {
                  newConvo.name = campaign.name;
                  newConvo.flashing = true;
                  await api.loadConversation(newConvo);
                  setConversations(prev => [...prev, newConvo]);
                  setToastTime(common_util.get_time_number());
                  setToastMsg(`In ${newConvo.name} Campaign ${from.name} says: ${processed.message}`);
                }
              }
            }
            setReloading(true);
            ping();
          }
        }
      }
    }
  }, []);

  // Get messages directly to this user.
  useUserReceivableAlerts(api.user, onUserReceivable);
  useAdminReceivableAlerts(api.user, onAdminReceivable);
  useCampaignsReceivableAlerts(api.user, onCampaignReceivable);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    // debugger;
    if (api.user) {
      const cst_group = await api.getPlayerObjects<ConvoSeenTimes>({ data_type: "convo_seen_times" });
      if (cst_group.length === 1) {
        api.user.convo_seen_times = cst_group[0];
      }
      let filter: any = { ids: { $in: [api.user_id] } };
      const user_conversations = await api.getObjects<Conversation>({ data_type: "conversation", filter });
      let admin_conversations: Conversation[] = [];
      if (api.user && api.user.is_admin) {
        // debugger;
        filter = { type: "Admin" }; 
        // Eventually the plan is to make it so admin conversations can be claimed by Admins, 
        // and then this would filter for ones that are unclaimed or are claimed by this Admin.
        admin_conversations = await api.getObjects<Conversation>({ data_type: "conversation", filter });
      }
      filter = { ids: { $in: api.my_campaigns.map(o => o._id) } };
      const campaign_conversations = await api.getObjects<Conversation>({ data_type: "conversation", filter });
      const new_conversations = [...user_conversations,...campaign_conversations,...admin_conversations];
      for (const convo of new_conversations) {
        const seen_time = api.user.convo_seen_times.seen_time(convo._id);
        if (convo.type === "Admin") {
          convo.hidden = false;
          await api.loadConversation(convo);
          if (seen_time === -1 || seen_time < convo.last_message_time) {
            convo.flashing = true;
          }
        } else {
          if (seen_time === -1 || seen_time < convo.last_message_time) {
            convo.hidden = false;
            convo.flashing = true;
            await api.loadConversation(convo);
          } else {
            convo.hidden = true;
          }
        }
      }
      console.log(new_conversations);
      setConversations(new_conversations);
    }
    setLoading(false);
  }, [api.user]);

  useEffect(() => {
    if (!fetched && api.user && api.campaigns_loaded) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, api.user, api.campaigns_loaded]);

  const deleteConvo = useCallback( async (convo: Conversation) => {
    await api.deleteObjects("receivable", convo.messages);
    const receivable = new Receivable();
    receivable.conversation_id = convo._id;
    receivable.from_id = api.user_id;
    receivable.admin = convo.type === "Admin";
    if (convo.type === "User" || convo.type === "Admin") {
      receivable.user_ids = convo.ids;
    } else {
      receivable.campaign_id = convo.ids[0];
    }
    receivable.sent_dt = common_util.get_time_number();
    receivable.message = "Conversation Deleted";
    receivable.origin_id = api.temp_id;
    receivable.type = "Chat";
    receivable.temp = true;
    await api.upsertObject("receivable", receivable);
    if (convo.type === "Admin") {
      // TODO: I need to make a version of deleteObject where instead of actually deleting it, 
      // It sets a zombie flag on it, and make getObject and getObjectCount on the backend automatically filter zombies out.
      await api.deleteObject("conversation", convo);
      setConversations(prev => [...prev.filter(o => o._id !== convo._id)]);
    } else {
      convo.hidden = true;
      convo.messages = [];
      convo.last_message_time = 0;
      await api.upsertObject("conversation", convo);
    }
    setDeletingConvo(undefined);
    setDeleting(false);
  }, [api]);

  useEffect(() => {
    if (deleting_convo && !deleting) {
      setDeleting(true);
      setTimeout(() => {
        deleteConvo(deleting_convo);
      }, 100);
    }
  }, [deleting_convo, deleting]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        '&:not(.disabled)': {
          backgroundColor: theme.palette.secondary.main,
        },
        '&.disabled': {
          backgroundColor: "gray",
          color: "black"
        }
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
          }}>
          { renderAvailableChats() }
        </ModalOrDrawer>
        <ToastBox
          openTime={ toast_time }
        >
          { toast_msg }
        </ToastBox>
      </>
    );
  }

  const renderAvailableChats = () => {
    if (api.user) {
      const my_campaigns = api.user.my_campaigns.filter(o => !o.archived);
      const filtered_users = api.my_users;
      // debugger;
      return (
        <FlexBox direction="column">
          <FlexBox>
            <InfoBox label="Start a Conversation" info_name="Conversations">
              <>
                <p>
                  You can use this tool to chat with a System Admin, 
                  or with anyone in a campaign with you, 
                  or have a group chat with everyone in a campaign.
                </p>
                <p>
                  If a user you are chatting with is also online and logged into the website, 
                  they will be notified immediately when the chat comes in. 
                  If they are not online and logged in, then the next time they are, they will be notified.
                </p>
                <p>
                  Chatting with a System Admin should only be done to ask for help, report bugs, or make feature requests.
                </p>
                <p>
                  The chat functionality is currently limited only to text.
                </p>
              </>
            </InfoBox>
          </FlexBox>
          { !api.user.is_admin &&
            <FlexBox className="padded">
              <FlexBox className={cx(classes.root, `padded clickable ${ processing ? "disabled" : ""}`)} onClick={ async () => {
                if (api.user) {
                  setProcessing(true);
                  let convo = conversationsRef.current.find(o => o.type === "Admin" && common_util.listsMatch(o.ids, [api.user_id]));
                  if (convo) {
                    convo.open = true;
                    convo.hidden = false;
                    await api.loadConversation(convo);
                  } else {
                    convo = new Conversation();
                    convo.type = "Admin";
                    convo.ids = [api.user_id];
                    convo.name = "System Admin";
                    convo.open = true;
                    // debugger;
                    await api.upsertObject("conversation", convo);
                    setConversations(prev => [...prev, convo!]);
                  }
                  setProcessing(false);
                  setOpen(false);
                  ping();
                }
              }}>
                System Admin
              </FlexBox>
            </FlexBox>
          }
          <FlexBox className="header centered">Campaigns</FlexBox>
          { my_campaigns.length > 0 && renderCampaigns(my_campaigns) }
          <FlexBox className="header centered">Users</FlexBox>
          { filtered_users.map(user => {
            return (
              <FlexBox key={user._id} className="padded">
                <FlexBox className={cx(classes.root, `padded clickable ${ processing ? "disabled" : ""}`)} onClick={ async () => {
                  if (api.user) {
                    setProcessing(true);
                    let convo = conversationsRef.current.find(o => common_util.listsMatch(o.ids, [user._id, api.user_id]));
                    if (convo) {
                      convo.open = true;
                      convo.hidden = false;
                      await api.loadConversation(convo);
                    } else {
                      convo = new Conversation();
                      convo.type = "User";
                      convo.ids = [user._id, api.user_id];
                      convo.name = user.name;
                      convo.open = true;
                      // debugger;
                      await api.upsertObject("conversation", convo);
                      setConversations(prev => [...prev, convo!]);
                    }
                    setProcessing(false);
                    setOpen(false);
                    ping();
                  }
                }}>
                  { user.name }
                </FlexBox>
              </FlexBox>
            );
          })}
        </FlexBox>
      );
    }
    return <></>;
  }

  const renderCampaigns = (my_campaigns: Campaign[]) => {
    return my_campaigns.map(campaign => {
      return (
        <FlexBox key={campaign._id} className="padded">
          <FlexBox className={cx(classes.root, `padded clickable ${ processing ? "disabled" : ""}`)} onClick={ async () => {
            if (api.user) {
              setProcessing(true);
              // debugger;
              let convo = conversationsRef.current.find(o => o.ids.length === 1 && o.ids.includes(campaign._id));
              if (convo) {
                convo.open = true;
                convo.hidden = false;
                await api.loadConversation(convo);
              } else {
                const newConvo = new Conversation();
                newConvo.type = "Campaign";
                newConvo.ids = [campaign._id];
                newConvo.name = campaign.name;
                newConvo.open = true;
                await api.upsertObject("conversation", newConvo);
                setConversations(prev => [...prev, newConvo]);
              }
              setProcessing(false);
              setOpen(false);
              ping();
            }
          }}>
            { campaign.name }
          </FlexBox>
        </FlexBox>
      );
    });
  }

  const renderChatButton = () => {
    if (api.user) {
      return (
        <Tooltip title={`Chat`}>
          <Fab size="small" color="primary" style={{marginLeft: "8px"}}
            onClick={ () => {
              setOpen(true);
            }}>
            <Comment />
          </Fab>
        </Tooltip>
      ); 
    }
    return null;
  }

  const renderConversations = () => {
    console.log(conversations);
    return conversations.map(convo => renderConversation(convo));
  }

  const renderConversation = (convo: Conversation, admin_conversation: boolean = false) => {
    if (!convo.hidden) {
      return (
        <FlexBox key={convo._id}>
          <ChatBox 
            convo={convo} 
            ping_time={ping_time} 
            onDelete={ async () => {
              setDeletingConvo(convo);
              setReloading(true);
            }}
          />
        </FlexBox>
      );
    }
    return null;
  }

  const renderChatsBar = () => {
    if (api.user) {
      return (
        <div className="bottom-bar">
          <div className="bottom-bar-content">
            <FlexBox direction="row" style={{ float: "right" }}>
              { renderConversations() }
              { renderChatButton() }
            </FlexBox>
          </div>
        </div>
      ); 
    }
    return null;
  }

  return (
    <>
      { renderChatsBar() }
      { renderExtras() }
    </>
  );
}